import React__default, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { SurveyThemeWrapper } from '@sm/question-ui/theme-respondent';
import { d as declareVisualization, i as isRequired, t as trimOtherValues, e as equalsIgnoreOrder, b as trimAllValues, g as getAnswerLayoutColumns, p as providerFactory } from './utils-78236cfc.js';
import { Dropdown, CommentBox, SingleTextbox, MultipleChoice, Checkbox, TextPresentational, ImagePresentational, ImageChoice } from '@sm/question-ui/respondent-survey';
import { dropdownRespondentSurveyValidation, commentBoxRespondentSurveyValidation, singleTextboxRespondentSurveyValidation, multipleChoiceRespondentSurveyValidation, checkBoxRespondentSurveyValidation, rankingRespondentSurveyValidation, imageChoiceRespondentSurveyValidation } from '@sm/question-definitions';
export { S as SingleQuestionController } from './controllers-28373cec.js';
import './vendor-4c16529b.js';
import '@sm/question-ui/helpers';

const useShowOkButton = (active, dirty) => {
  const [showOkButton, setShowOkButton] = useState(false);
  useEffect(() => {
    if (!active) {
      setShowOkButton(false);
    } else if (dirty) {
      setShowOkButton(true);
    }
  }, [active, dirty, setShowOkButton]);
  return showOkButton;
};

function useFieldIsDirty(active) {
  const [fieldDirty, setFieldIsDirty] = useState(false);
  useEffect(() => {
    setFieldIsDirty(false);
  }, [active]);
  return [fieldDirty, setFieldIsDirty];
}

const useWithValidatorRegistration = (question, values, touched, registerValidator, unregisterValidator, questionTypeSpecificValidation) => {
  const {
    id
  } = question;
  const validationFunction = useRef();
  validationFunction.current = useCallback(forceValidation => {
    return questionTypeSpecificValidation(question, values, touched || forceValidation);
  }, [question, values, touched, questionTypeSpecificValidation]);
  useEffect(() => {
    if (registerValidator) {
      registerValidator(id, [forceValidation => {
        if (!validationFunction.current) {
          return null;
        }

        return validationFunction.current(forceValidation);
      }]);
    }

    return () => unregisterValidator === null || unregisterValidator === void 0 ? void 0 : unregisterValidator(id);
  }, [id, registerValidator, unregisterValidator]);
}; // Visualization for Question of type Dropdown


function RespondentDropdownVisualization({
  question,
  displayOptions,
  upstreamActions,
  toExternalQuestion,
  settings: {
    survey
  }
}) {
  const {
    id,
    questionNumber,
    title,
    layout
  } = question;
  const {
    active,
    errors: upstreamErrors,
    answer: upstreamAnswer,
    registerValidator,
    unregisterValidator
  } = displayOptions || {};
  const error = (upstreamErrors !== null && upstreamErrors !== void 0 ? upstreamErrors : [])[0];
  const padding = survey.isClassic ? layout === null || layout === void 0 ? void 0 : layout.spacing : undefined;
  const width = survey.isClassic ? layout === null || layout === void 0 ? void 0 : layout.width : undefined;
  const defaultAnswer = useMemo(() => {
    var _a;

    return {
      questionId: id,
      family: question.family,
      variant: question.variant,
      touched: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.touched),
      isDirty: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.isDirty),
      values: (_a = upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.values) !== null && _a !== void 0 ? _a : []
    };
  }, [id, upstreamAnswer, question]);
  const [answer, setAnswer] = useState(defaultAnswer);
  useWithValidatorRegistration(question, answer.values, answer.touched, registerValidator, unregisterValidator, dropdownRespondentSurveyValidation);
  /**
   * Determines if the field is dirty (changed) internally (not for validation)
   * The OK Button is visible when change is made to the field only. The dirty
   * state resets (false) when activation of the question changes.
   */

  const [fieldDirty, setFieldIsDirty] = useFieldIsDirty(!!active);
  const showOkButton = useShowOkButton(!!active, fieldDirty);
  const required = isRequired(question);
  const questionTitle = {
    id: `question-title-${id}`,
    heading: title,
    number: Number(questionNumber),
    numbered: survey.numberingEnabled,
    required: survey.asteriskEnabled && required
  };
  const questionOkButton = {
    visible: !survey.isClassic && showOkButton,
    text: survey.okLabel,
    onClick: displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.onSubmit
  };
  const onLeaveQuestion = useCallback(() => {
    var _a;

    (_a = upstreamActions.onLeaveQuestion) === null || _a === void 0 ? void 0 : _a.call(upstreamActions, toExternalQuestion(question), answer);
  }, [question, upstreamActions, answer, toExternalQuestion]);
  return React__default.createElement(Dropdown, {
    id: id,
    title: questionTitle,
    okButton: questionOkButton,
    required: required,
    error: error === null || error === void 0 ? void 0 : error.detail,
    onLeave: onLeaveQuestion,
    onChange: values => {
      setFieldIsDirty(true);
      setAnswer(response => {
        const trimmedValues = trimOtherValues(values);
        const isDirty = !equalsIgnoreOrder(defaultAnswer.values, trimmedValues);
        return { ...response,
          values: trimmedValues,
          isDirty,
          touched: true
        };
      });
    },
    choices: question.answers,
    choiceOther: question.answerOther,
    choiceComment: question.answerComment,
    responses: defaultAnswer.values,
    ...{
      padding,
      width
    }
  });
}

var AnswerDropdownVisualization = declareVisualization(['dropdown'], ['respondent-survey'], RespondentDropdownVisualization);

function RespondentCommentBoxVisualization({
  upstreamActions,
  question,
  settings: {
    survey
  },
  toExternalQuestion,
  displayOptions
}) {
  const {
    id,
    questionNumber,
    title,
    layout
  } = question;
  const {
    active,
    errors: upstreamErrors,
    answer: upstreamAnswer,
    registerValidator,
    unregisterValidator
  } = displayOptions || {};
  const error = (upstreamErrors !== null && upstreamErrors !== void 0 ? upstreamErrors : [])[0];
  const padding = survey.isClassic ? layout.spacing : undefined;
  const cols = survey.isClassic ? layout === null || layout === void 0 ? void 0 : layout.charsCount : undefined;
  const rows = survey.isClassic ? layout === null || layout === void 0 ? void 0 : layout.linesCount : undefined;
  const defaultAnswer = useMemo(() => {
    var _a;

    return {
      questionId: id,
      family: question.family,
      variant: question.variant,
      touched: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.touched),
      isDirty: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.isDirty),
      values: (_a = upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.values) !== null && _a !== void 0 ? _a : []
    };
  }, [id, upstreamAnswer, question]);
  const [answer, setAnswer] = useState(defaultAnswer);
  useWithValidatorRegistration(question, answer.values, answer.touched, registerValidator, unregisterValidator, commentBoxRespondentSurveyValidation);
  const [fieldDirty, setFieldIsDirty] = useFieldIsDirty(!!active);
  const showOkButton = useShowOkButton(!!active, fieldDirty);
  const questionTitle = {
    id: `question-title-${id}`,
    heading: title,
    number: Number(questionNumber),
    numbered: survey.numberingEnabled,
    required: survey.asteriskEnabled && !!question.required
  };
  const questionOkButton = {
    visible: !survey.isClassic && showOkButton,
    text: survey.okLabel,
    onClick: displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.onSubmit
  };
  const onLeaveQuestion = useCallback(() => {
    if (upstreamActions.onLeaveQuestion) {
      upstreamActions.onLeaveQuestion(toExternalQuestion(question), answer);
    }
  }, [question, upstreamActions, answer, toExternalQuestion]);
  return React__default.createElement(CommentBox, {
    id: id,
    title: questionTitle,
    onLeave: onLeaveQuestion,
    okButton: questionOkButton,
    error: error === null || error === void 0 ? void 0 : error.detail,
    ...{
      padding,
      cols,
      rows
    },
    onChange: values => {
      setFieldIsDirty(true);
      setAnswer(response => {
        // Dirty only if change ≠ default
        const isDirty = !equalsIgnoreOrder(defaultAnswer.values, values);
        return { ...response,
          values,
          isDirty,
          touched: true
        };
      });
    },
    responses: defaultAnswer.values,
    required: isRequired(question)
  });
}

var AnswerCommentBoxVisualization = declareVisualization(['comment_box'], ['respondent-survey'], RespondentCommentBoxVisualization); // Visualization for Question of type SingleTextbox

function RespondentSingleTextboxVisualization({
  upstreamActions,
  question,
  settings: {
    survey
  },
  toExternalQuestion,
  displayOptions
}) {
  const {
    id,
    questionNumber,
    title,
    layout
  } = question;
  const {
    active,
    errors: upstreamErrors,
    answer: upstreamAnswer,
    registerValidator,
    unregisterValidator
  } = displayOptions || {};
  const error = (upstreamErrors !== null && upstreamErrors !== void 0 ? upstreamErrors : [])[0];
  const padding = survey.isClassic ? layout === null || layout === void 0 ? void 0 : layout.spacing : undefined;
  const size = survey.isClassic ? layout === null || layout === void 0 ? void 0 : layout.charsCount : undefined;
  const defaultAnswer = useMemo(() => {
    var _a;

    return {
      questionId: id,
      family: question.family,
      variant: question.variant,
      touched: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.touched),
      isDirty: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.isDirty),
      values: (_a = upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.values) !== null && _a !== void 0 ? _a : []
    };
  }, [id, upstreamAnswer, question]);
  const [answer, setAnswer] = useState(defaultAnswer);
  useWithValidatorRegistration(question, answer.values, answer.touched, registerValidator, unregisterValidator, singleTextboxRespondentSurveyValidation);
  /**
   * Determines if the field is dirty (changed) internally (not for validation)
   * The OK Button is visible when change is made to the field only. The dirty
   * state resets (false) when activation of the question changes.
   */

  const [fieldDirty, setFieldIsDirty] = useFieldIsDirty(!!active);
  const showOkButton = useShowOkButton(!!active, fieldDirty);
  const questionTitle = {
    id: `question-title-${id}`,
    heading: title,
    number: Number(questionNumber),
    numbered: survey.numberingEnabled,
    required: survey.asteriskEnabled && !!question.required
  };
  const questionOkButton = {
    visible: !survey.isClassic && showOkButton,
    text: survey.okLabel,
    onClick: displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.onSubmit
  };
  const onLeaveQuestion = useCallback(() => {
    if (upstreamActions.onLeaveQuestion) {
      upstreamActions.onLeaveQuestion(toExternalQuestion(question), answer);
    }
  }, [question, upstreamActions, answer, toExternalQuestion]);
  return React__default.createElement(SingleTextbox, {
    id: id,
    title: questionTitle,
    onLeave: onLeaveQuestion,
    okButton: questionOkButton,
    error: error === null || error === void 0 ? void 0 : error.detail,
    ...{
      padding,
      size
    },
    onChange: values => {
      setFieldIsDirty(true);
      setAnswer(response => {
        const trimmedValues = trimAllValues(values); // Dirty only if change ≠ default

        const isDirty = !equalsIgnoreOrder(defaultAnswer.values, trimmedValues);
        return { ...response,
          values: trimmedValues,
          isDirty,
          touched: true
        };
      });
    },
    responses: defaultAnswer.values,
    required: isRequired(question)
  });
}

var AnswerSingleTextboxVisualization = declareVisualization(['single_textbox'], ['respondent-survey'], RespondentSingleTextboxVisualization); // Visualization for Question of type MultipleChoice

function RespondentMultipleChoiceVisualization({
  question,
  displayOptions,
  upstreamActions,
  toExternalQuestion,
  settings: {
    survey
  }
}) {
  const {
    id,
    questionNumber,
    title,
    layout
  } = question;
  const {
    active,
    errors: upstreamErrors,
    answer: upstreamAnswer,
    registerValidator,
    unregisterValidator
  } = displayOptions || {};
  const error = (upstreamErrors !== null && upstreamErrors !== void 0 ? upstreamErrors : [])[0];
  const padding = survey.isClassic ? layout.spacing : undefined;
  const columnLayout = getAnswerLayoutColumns(layout === null || layout === void 0 ? void 0 : layout.answerLayout);
  const width = survey.isClassic ? layout === null || layout === void 0 ? void 0 : layout.width : undefined;
  const defaultAnswer = useMemo(() => {
    var _a;

    return {
      questionId: id,
      family: question.family,
      variant: question.variant,
      touched: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.touched),
      isDirty: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.isDirty),
      values: (_a = upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.values) !== null && _a !== void 0 ? _a : []
    };
  }, [id, upstreamAnswer, question]);
  const [answer, setAnswer] = useState(defaultAnswer);
  useWithValidatorRegistration(question, answer.values, answer.touched, registerValidator, unregisterValidator, multipleChoiceRespondentSurveyValidation);
  /**
   * Determines if the field is dirty (changed) internally (not for validation)
   * The OK Button is visible when change is made to the field only. The dirty
   * state resets (false) when activation of the question changes.
   */

  const [fieldDirty, setFieldIsDirty] = useFieldIsDirty(!!active);
  const showOkButton = useShowOkButton(!!active, fieldDirty);
  const questionTitle = {
    id: `question-title-${id}`,
    heading: title,
    number: Number(questionNumber),
    numbered: survey.numberingEnabled,
    required: survey.asteriskEnabled && !!question.required
  };
  const questionOkButton = {
    visible: !survey.isClassic && showOkButton,
    text: survey.okLabel,
    onClick: displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.onSubmit
  };
  const onLeaveQuestion = useCallback(() => {
    var _a;

    (_a = upstreamActions.onLeaveQuestion) === null || _a === void 0 ? void 0 : _a.call(upstreamActions, toExternalQuestion(question), answer);
  }, [question, upstreamActions, answer, toExternalQuestion]);
  return React__default.createElement(MultipleChoice // required
  , {
    // required
    id: id,
    title: questionTitle,
    onLeave: onLeaveQuestion,
    // optional
    okButton: questionOkButton,
    error: error === null || error === void 0 ? void 0 : error.detail,
    ...{
      padding,
      width
    },
    onChange: values => {
      setFieldIsDirty(true);
      setAnswer(response => {
        const trimmedValues = trimOtherValues(values); // Dirty only if change ≠ default

        const isDirty = !equalsIgnoreOrder(defaultAnswer.values, trimmedValues);
        return { ...response,
          values: trimmedValues,
          isDirty,
          touched: true
        };
      });
    },
    choices: question.answers,
    choiceNoneOfTheAbove: question.answerNoneOfTheAbove ? { ...question.answerNoneOfTheAbove,
      type: 'NOTA'
    } : undefined,
    choiceOther: question.answerOther,
    choiceComment: question.answerComment,
    responses: defaultAnswer.values,
    required: isRequired(question),
    columnLayout: columnLayout
  });
}

var AnswerMultipleChoiceVisualization = declareVisualization(['multiple_choice'], ['respondent-survey'], RespondentMultipleChoiceVisualization); // Visualization for Question of type Checkbox

function RespondentCheckboxVisualization({
  question,
  displayOptions,
  upstreamActions,
  toExternalQuestion,
  settings: {
    survey
  }
}) {
  const {
    id,
    questionNumber,
    title,
    layout
  } = question;
  const {
    active,
    errors: upstreamErrors,
    answer: upstreamAnswer,
    registerValidator,
    unregisterValidator
  } = displayOptions || {};
  const error = (upstreamErrors !== null && upstreamErrors !== void 0 ? upstreamErrors : [])[0];
  const padding = survey.isClassic ? layout.spacing : undefined;
  const width = survey.isClassic ? layout === null || layout === void 0 ? void 0 : layout.width : undefined;
  const columnLayout = getAnswerLayoutColumns(layout === null || layout === void 0 ? void 0 : layout.answerLayout);
  const defaultAnswer = useMemo(() => {
    var _a;

    return {
      questionId: id,
      family: question.family,
      variant: question.variant,
      touched: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.touched),
      isDirty: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.isDirty),
      values: (_a = upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.values) !== null && _a !== void 0 ? _a : []
    };
  }, [id, upstreamAnswer, question]);
  const [answer, setAnswer] = useState(defaultAnswer);
  useWithValidatorRegistration(question, answer.values, answer.touched, registerValidator, unregisterValidator, checkBoxRespondentSurveyValidation);
  /**
   * Determines if the field is dirty (changed) internally (not for validation)
   * The OK Button is visible when change is made to the field only. The dirty
   * state resets (false) when activation of the question changes.
   */

  const [fieldDirty, setFieldIsDirty] = useFieldIsDirty(!!active);
  const showOkButton = useShowOkButton(!!active, fieldDirty);
  const required = isRequired(question);
  const questionTitle = {
    id: `question-title-${id}`,
    heading: title,
    number: Number(questionNumber),
    numbered: survey.numberingEnabled,
    required: survey.asteriskEnabled && required
  };
  const questionOkButton = {
    visible: !survey.isClassic && showOkButton,
    text: survey.okLabel,
    onClick: displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.onSubmit
  };
  const onLeaveQuestion = useCallback(() => {
    var _a;

    (_a = upstreamActions.onLeaveQuestion) === null || _a === void 0 ? void 0 : _a.call(upstreamActions, toExternalQuestion(question), answer);
  }, [question, upstreamActions, answer, toExternalQuestion]);
  return React__default.createElement(Checkbox // required
  , {
    // required
    id: id,
    title: questionTitle,
    onLeave: onLeaveQuestion,
    // optional
    okButton: questionOkButton,
    error: error === null || error === void 0 ? void 0 : error.detail,
    ...{
      padding,
      width
    },
    onChange: values => {
      setFieldIsDirty(true);
      setAnswer(response => {
        const trimmedValues = trimOtherValues(values); // Dirty only if change ≠ default

        const isDirty = !equalsIgnoreOrder(defaultAnswer.values, trimmedValues);
        return { ...response,
          values: trimmedValues,
          isDirty,
          touched: true
        };
      });
    },
    choices: question.answers,
    choiceNoneOfTheAbove: { ...question.answerNoneOfTheAbove,
      type: 'NOTA'
    },
    choiceOther: question.answerOther,
    choiceComment: question.answerComment,
    responses: answer.values,
    required: required,
    columnLayout: columnLayout
  });
}

var AnswerCheckboxVisualization = declareVisualization(['check_box'], ['respondent-survey'], RespondentCheckboxVisualization); // Visualization for Question of type Ranking

function RespondentRankingVisualization({
  question,
  displayOptions,
  upstreamActions,
  toExternalQuestion,
  settings: {
    survey
  }
}) {
  const {
    id,
    questionNumber,
    title,
    layout
  } = question;
  const {
    active,
    errors: upstreamErrors,
    answer: upstreamAnswer,
    registerValidator,
    unregisterValidator
  } = displayOptions || {};
  const error = (upstreamErrors !== null && upstreamErrors !== void 0 ? upstreamErrors : [])[0];
  const padding = survey.isClassic ? layout === null || layout === void 0 ? void 0 : layout.spacing : undefined;
  const width = survey.isClassic ? layout === null || layout === void 0 ? void 0 : layout.width : undefined;
  const defaultAnswer = useMemo(() => {
    var _a;

    return {
      questionId: id,
      family: question.family,
      variant: question.variant,
      touched: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.touched),
      isDirty: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.isDirty),
      values: (_a = upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.values) !== null && _a !== void 0 ? _a : []
    };
  }, [id, upstreamAnswer, question]);
  const [answer, setAnswer] = useState(defaultAnswer);
  useWithValidatorRegistration(question, answer.values, answer.touched, registerValidator, unregisterValidator, rankingRespondentSurveyValidation);
  /**
   * Determines if the field is dirty (changed) internally (not for validation)
   * The OK Button is visible when change is made to the field only. The dirty
   * state resets (false) when activation of the question changes.
   */

  const [fieldDirty, setFieldIsDirty] = useFieldIsDirty(!!active);
  const showOkButton = useShowOkButton(!!active, fieldDirty);
  const required = isRequired(question);
  const questionTitle = {
    id: `question-title-${id}`,
    heading: title,
    number: Number(questionNumber),
    numbered: survey.numberingEnabled,
    required: survey.asteriskEnabled && required
  };
  const questionOkButton = {
    visible: !survey.isClassic && showOkButton,
    text: survey.okLabel,
    onClick: displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.onSubmit
  };
  const onLeaveQuestion = useCallback(() => {
    var _a;

    (_a = upstreamActions.onLeaveQuestion) === null || _a === void 0 ? void 0 : _a.call(upstreamActions, toExternalQuestion(question), answer);
  }, [question, upstreamActions, answer, toExternalQuestion]);
  return React__default.createElement("div", null, React__default.createElement("h1", null, "Tier 2 Not Implemented yet"), "TODO: Wire up Tier 2 and add Tests", React__default.createElement("br", null), React__default.createElement("pre", null, React__default.createElement("code", {
    onBlur: () => onLeaveQuestion(),
    onFocus: () => {
      setFieldIsDirty(true);
      setAnswer({});
    }
  }, `${JSON.stringify(questionTitle, null, 2)}\n`, `${JSON.stringify(questionOkButton, null, 2)}\n`, `${JSON.stringify(error, null, 2)}\n`, `${JSON.stringify(padding, null, 2)}\n`, `${JSON.stringify(width, null, 2)}`)));
}

var AnswerRankingVisualization = declareVisualization(['ranking'], ['respondent-survey'], RespondentRankingVisualization);
/** Visualization for Question of type Image & Text Presentation */

function RespondentPresentationVisualization({
  question,
  displayOptions,
  settings: {
    survey
  }
}) {
  var _a;

  const {
    id,
    layout,
    variant,
    heading: {
      default: {
        text,
        image
      }
    }
  } = question;
  const imageProps = {
    id: image === null || image === void 0 ? void 0 : image.id,
    src: image === null || image === void 0 ? void 0 : image.url,
    alt: (_a = image === null || image === void 0 ? void 0 : image.alt) !== null && _a !== void 0 ? _a : undefined
  };
  const questionOkButton = {
    visible: !survey.isClassic && !!(displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.active),
    text: survey.okLabel,
    onClick: displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.onSubmit
  };
  const padding = survey.isClassic ? layout === null || layout === void 0 ? void 0 : layout.spacing : undefined;
  const presentationProps = {
    id,
    text,
    padding,
    okButton: questionOkButton
  };
  return React__default.createElement("div", null, variant === 'TEXT' ? React__default.createElement(TextPresentational, { ...presentationProps
  }) : React__default.createElement(ImagePresentational, { ...presentationProps,
    image: imageProps
  }));
}

var AnswerPresentationVisualization = declareVisualization(['text_presentation', 'image_presentation'], ['respondent-survey'], RespondentPresentationVisualization); // Visualization for Question of type ImageChoice

function RespondentImageChoiceVisualization({
  question,
  displayOptions,
  upstreamActions,
  toExternalQuestion,
  settings: {
    survey
  }
}) {
  const {
    id,
    questionNumber,
    title
  } = question;
  const {
    active,
    errors: upstreamErrors,
    answer: upstreamAnswer,
    registerValidator,
    unregisterValidator
  } = displayOptions || {};
  const error = (upstreamErrors !== null && upstreamErrors !== void 0 ? upstreamErrors : [])[0];
  const defaultAnswer = useMemo(() => {
    var _a;

    return {
      questionId: id,
      family: question.family,
      variant: question.variant,
      touched: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.touched),
      isDirty: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.isDirty),
      values: (_a = upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.values) !== null && _a !== void 0 ? _a : []
    };
  }, [id, upstreamAnswer, question]);
  const [answer, setAnswer] = useState(defaultAnswer);
  useWithValidatorRegistration(question, answer.values, answer.touched, registerValidator, unregisterValidator, imageChoiceRespondentSurveyValidation);
  /**
   * Determines if the field is dirty (changed) internally (not for validation)
   * The OK Button is visible when change is made to the field only. The dirty
   * state resets (false) when activation of the question changes.
   */

  const [fieldDirty, setFieldIsDirty] = useFieldIsDirty(!!active);
  const showOkButton = useShowOkButton(!!active, fieldDirty);
  const required = isRequired(question);
  const questionTitle = {
    id: `question-title-${id}`,
    heading: title,
    number: Number(questionNumber),
    numbered: survey.numberingEnabled,
    required: survey.asteriskEnabled && required
  };
  const questionOkButton = {
    visible: !survey.isClassic && showOkButton,
    text: survey.okLabel,
    onClick: displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.onSubmit
  };
  const onLeaveQuestion = useCallback(() => {
    var _a;

    (_a = upstreamActions.onLeaveQuestion) === null || _a === void 0 ? void 0 : _a.call(upstreamActions, toExternalQuestion(question), answer);
  }, [question, upstreamActions, answer, toExternalQuestion]);
  let multiple = false;

  if (question.variant === 'MANY_ANSWERS_IMAGE') {
    multiple = true;
  }

  return React__default.createElement(ImageChoice, {
    id: id,
    title: questionTitle,
    onLeave: onLeaveQuestion,
    okButton: questionOkButton,
    error: error === null || error === void 0 ? void 0 : error.detail,
    onChange: values => {
      setFieldIsDirty(true);
      setAnswer(response => {
        // Dirty only if change ≠ default
        const isDirty = !equalsIgnoreOrder(defaultAnswer.values, values);
        return { ...response,
          values,
          isDirty,
          touched: true
        };
      });
    },
    choices: question.answersImage,
    choiceNoneOfTheAbove: { ...question.answerNoneOfTheAbove,
      type: 'NOTA'
    },
    multiple: multiple,
    responses: defaultAnswer.values,
    required: required
  });
}

var AnswerImageChoiceVisualization = declareVisualization(['image_choice'], ['respondent-survey'], RespondentImageChoiceVisualization);
var visualizationList = [AnswerSingleTextboxVisualization, AnswerCommentBoxVisualization, AnswerDropdownVisualization, AnswerMultipleChoiceVisualization, AnswerCheckboxVisualization, AnswerRankingVisualization, AnswerPresentationVisualization, AnswerImageChoiceVisualization]; // Creates the helper to store the configuration

const ProviderHelper = providerFactory(visualizationList); // Declares the Provider Component itself, with the proper API

function RespondentSurveyCapabilityProvider({
  upstreamActions,
  settings,
  children,
  renderProps
}) {
  // here you can keep capability-specific state, translate actions or make other modifications
  // that are appropriate to your capability-specific behaviour
  return React__default.createElement(ProviderHelper, {
    value: {
      capability: 'respondent-survey',
      upstreamActions,
      renderProps,
      settings
    }
  }, React__default.createElement(SurveyThemeWrapper, null, children));
}

export { RespondentSurveyCapabilityProvider as RespondentCapabilityProvider, RespondentSurveyCapabilityProvider, visualizationList as RespondentVisualizations };